/**
 * Labelled disclosure that reveals a detailed financial statement in place,
 * directly beneath the analysis it supports (MOR-1281).
 */

import { ChevronRight } from "lucide-react";
import { cn } from "@/utils/misc";

interface StatementDisclosureProps {
  /** DOM id of the container; the button's `aria-controls` target. */
  id: string;
  /** Statement name, e.g. "income statement" → "View income statement". */
  label: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

/** Renders the toggle button and, when open, the disclosed statement. */
export function StatementDisclosure({
  id,
  label,
  open,
  onOpenChange,
  children,
}: StatementDisclosureProps) {
  const panelId = `${id}-panel`;
  return (
    <div id={id} className="scroll-mt-40 border-t border-border">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => onOpenChange(!open)}
        className="flex w-full items-center gap-1.5 px-5 py-2 text-left text-xs font-medium text-violet-700 transition-colors hover:bg-muted/30 dark:text-violet-300"
      >
        <ChevronRight
          className={cn(
            "h-3.5 w-3.5 shrink-0 transition-transform duration-200",
            open && "rotate-90",
          )}
        />
        {open ? `Hide ${label}` : `View ${label}`}
      </button>
      {open && (
        <div id={panelId} role="region" aria-label={label}>
          {children}
        </div>
      )}
    </div>
  );
}
